import { useState } from 'react';
import {
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Stack,
  Paper,
  Table,
  Checkbox,
  Badge,
  Text,
  ActionIcon,
  Menu,
  Drawer,
  Tabs,
  Grid,
  Card,
  Skeleton,
  Alert,
  Pagination,
  Progress,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconRefresh,
  IconEye,
  IconDotsVertical,
  IconDeviceDesktop,
  IconNetwork,
  IconCpu,
  IconAlertTriangle,
  IconCloud,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconTag,
  IconArchive,
  IconPackage,
  IconShieldCheck,
  IconShieldX,
  IconQuestionMark,
  IconTrash,
  IconDownload,
  IconRocket,
  IconSettings,
  IconUsers,
  IconPlus,
  IconWifi,
  IconDoor,
  IconBolt,
  IconKey,
  IconRouter,
  IconEdit,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useDevices, useDevice, useTriggerRescan, useSites, useRooms, useUpdateDeviceSite, useUpdateDeviceRoom, useDeviceSoftware, useDeviceDrift, useDownloadRemediationScript, useDeployRemediation, useSetSoftwareStatus, useDeviceGroups, useAddDevicesToGroup, useDeviceStatuses, useCheckDeviceStatusBulk, useWakeOnLanBulk, useRelayAgents, useDeviceCredentials, type SoftwareItem, type DriftChangeEvent, type DeviceStatus as DeviceStatusType, type RelayAgent } from '@/hooks';
import { Modal } from '@mantine/core';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { OnlineStatusBadge, PowerControlMenu, DeviceCredentialsModal, DeviceEditModal } from '@/components/devices';

export function DevicesPage() {
  const navigate = useNavigate();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure();
  const [groupModalOpened, { open: openGroupModal, close: closeGroupModal }] = useDisclosure();
  const [credentialsModalOpened, { open: openCredentialsModal, close: closeCredentialsModal }] = useDisclosure();
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Filters state
  const [filters, setFilters] = useState({
    site: '',
    room: '',
    deviceType: '',
    hasActiveTask: '',
    driftSeverity: '',
  });

  // Fetch devices
  const { data, isLoading, isError, refetch } = useDevices({
    page,
    search: searchQuery,
    ...filters,
  });

  // Fetch device statuses
  const { data: deviceStatuses, refetch: refetchStatuses } = useDeviceStatuses();
  const checkStatusBulk = useCheckDeviceStatusBulk();
  const wakeOnLanBulk = useWakeOnLanBulk();

  // Build a map of deviceId -> status for quick lookup
  const statusMap = new Map<string, DeviceStatusType>();
  deviceStatuses?.forEach((status) => {
    statusMap.set(status.deviceId, status);
  });

  // Trigger rescan mutation
  const triggerRescan = useTriggerRescan();

  // Device groups
  const { data: groupsData } = useDeviceGroups();
  const addToGroup = useAddDevicesToGroup();

  // Sites and rooms for filters
  const { data: sites } = useSites();
  const { data: rooms } = useRooms(filters.site || undefined);

  const handleTriggerRescan = async (deviceId: string) => {
    try {
      await triggerRescan.mutateAsync({ deviceId });
      notifications.show({
        title: 'Rescan triggered',
        message: 'OCS inventory rescan has been requested',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to trigger rescan',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const handlePreviewDevice = (deviceId: string) => {
    navigate('/preview', { state: { deviceIds: [deviceId] } });
  };

  const handleRowClick = (deviceId: string) => {
    setSelectedDevice(deviceId);
    openDrawer();
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && data?.devices) {
      setSelectedRows(data.devices.map((d) => d.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (deviceId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, deviceId]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== deviceId));
    }
  };

  const handlePreviewSelected = () => {
    if (selectedRows.length > 0) {
      navigate('/preview', { state: { deviceIds: selectedRows } });
    }
  };

  const handleCheckStatusSelected = async () => {
    if (selectedRows.length === 0) return;
    try {
      await checkStatusBulk.mutateAsync(selectedRows);
      refetchStatuses();
    } catch (error) {
      notifications.show({
        title: 'Status check failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const handleWakeOnLanSelected = async () => {
    if (selectedRows.length === 0) return;
    try {
      await wakeOnLanBulk.mutateAsync({ deviceIds: selectedRows });
    } catch (error) {
      notifications.show({
        title: 'Wake-on-LAN failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroupId || selectedRows.length === 0) return;

    try {
      const result = await addToGroup.mutateAsync({
        groupId: selectedGroupId,
        deviceIds: selectedRows,
      });
      notifications.show({
        title: 'Devices added to group',
        message: result.message,
        color: 'green',
      });
      closeGroupModal();
      setSelectedRows([]);
      setSelectedGroupId(null);
    } catch (error) {
      notifications.show({
        title: 'Failed to add devices',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const devices = data?.devices || [];
  const totalPages = data?.totalPages || 1;

  // Format relative time
  const formatLastSeen = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Devices</Title>
            <Text c="dimmed" size="sm">
              Manage and monitor your device fleet
            </Text>
          </div>

          <Group>
            {selectedRows.length > 0 && (
              <>
                <Button
                  variant="light"
                  color="yellow"
                  leftSection={<IconBolt size={16} />}
                  onClick={handleWakeOnLanSelected}
                  loading={wakeOnLanBulk.isPending}
                >
                  Wake Up
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconWifi size={16} />}
                  onClick={handleCheckStatusSelected}
                  loading={checkStatusBulk.isPending}
                >
                  Check Status
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconUsers size={16} />}
                  onClick={openGroupModal}
                >
                  Add to Group
                </Button>
                <Button
                  variant="filled"
                  leftSection={<IconEye size={16} />}
                  onClick={handlePreviewSelected}
                >
                  Preview {selectedRows.length} devices
                </Button>
              </>
            )}
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => { refetch(); refetchStatuses(); }}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Filters bar */}
        <Paper p="md" withBorder>
          <Group>
            <TextInput
              placeholder="Search hostname, MAC, serial..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: 300 }}
            />
            <Select
              placeholder="Site"
              data={[
                { value: '', label: 'All sites' },
                ...(sites?.map(s => ({ value: s.id, label: s.name })) || []),
              ]}
              value={filters.site}
              onChange={(v) => setFilters((f) => ({ ...f, site: v || '', room: '' }))}
              clearable
              w={150}
            />
            <Select
              placeholder="Room"
              data={[
                { value: '', label: 'All rooms' },
                ...(rooms?.map(r => ({ value: r.id, label: `${r.roomCode} - ${r.roomName}` })) || []),
              ]}
              value={filters.room}
              onChange={(v) => setFilters((f) => ({ ...f, room: v || '' }))}
              clearable
              w={180}
              disabled={!filters.site}
            />
            <Select
              placeholder="Device type"
              data={[
                { value: '', label: 'All types' },
                { value: 'desktop', label: 'Desktop' },
                { value: 'laptop', label: 'Laptop' },
                { value: 'server', label: 'Server' },
              ]}
              value={filters.deviceType}
              onChange={(v) => setFilters((f) => ({ ...f, deviceType: v || '' }))}
              clearable
              w={150}
            />
            <Select
              placeholder="Drift severity"
              data={[
                { value: '', label: 'Any drift' },
                { value: 'none', label: 'None' },
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ]}
              value={filters.driftSeverity}
              onChange={(v) => setFilters((f) => ({ ...f, driftSeverity: v || '' }))}
              clearable
              w={150}
            />
          </Group>
        </Paper>

        {/* Devices table */}
        <Paper withBorder>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <ErrorState
              message="Failed to load devices"
              onRetry={() => refetch()}
            />
          ) : devices.length === 0 ? (
            <EmptyState
              icon={<IconDeviceDesktop size={48} />}
              title="No devices found"
              description="Try adjusting your search or filters"
            />
          ) : (
            <>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        checked={selectedRows.length === devices.length}
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < devices.length
                        }
                        onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                        aria-label="Select all"
                      />
                    </Table.Th>
                    <Table.Th>Hostname</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Site</Table.Th>
                    <Table.Th>Room</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Identity</Table.Th>
                    <Table.Th>Data Quality</Table.Th>
                    <Table.Th>Last Seen</Table.Th>
                    <Table.Th>Drift</Table.Th>
                    <Table.Th w={80}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {devices.map((device) => (
                    <Table.Tr
                      key={device.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(device.id)}
                    >
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRows.includes(device.id)}
                          onChange={(e) =>
                            handleSelectRow(device.id, e.currentTarget.checked)
                          }
                          aria-label={`Select ${device.hostname}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <IconDeviceDesktop size={16} />
                          <Text fw={500}>{device.hostname}</Text>
                          {device.hasActiveTask && (
                            <Badge size="xs" color="yellow" variant="dot">
                              Task running
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const deviceStatus = statusMap.get(device.id);
                          return (
                            <OnlineStatusBadge
                              status={deviceStatus?.status || 'unknown'}
                              deviceStatus={deviceStatus}
                              size="xs"
                            />
                          );
                        })()}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{device.site}</Text>
                      </Table.Td>
                      <Table.Td>
                        {(device as any).roomName ? (
                          <Group gap="xs">
                            <IconDoor size={14} />
                            <Text size="sm">{(device as any).roomName}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">
                          {device.deviceType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          type="identityConfidence"
                          value={device.identityConfidence}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Progress
                            value={device.dataQualityScore}
                            size="sm"
                            w={60}
                            color={
                              device.dataQualityScore >= 80
                                ? 'green'
                                : device.dataQualityScore >= 60
                                ? 'yellow'
                                : 'red'
                            }
                          />
                          <Text size="xs" c="dimmed">
                            {device.dataQualityScore}%
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatLastSeen(device.lastSeenAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          type="driftSeverity"
                          value={device.driftSeverity}
                        />
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap="xs">
                          {(() => {
                            const deviceStatus = statusMap.get(device.id);
                            return (
                              <PowerControlMenu
                                deviceId={device.id}
                                deviceName={device.hostname}
                                status={deviceStatus?.status || 'unknown'}
                                deviceStatus={deviceStatus}
                                onStatusUpdated={() => refetchStatuses()}
                              />
                            );
                          })()}
                          <Menu position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="subtle" size="sm">
                                <IconDotsVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconEye size={14} />}
                                onClick={() => handlePreviewDevice(device.id)}
                              >
                                Preview changes
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconRefresh size={14} />}
                                onClick={() => handleTriggerRescan(device.id)}
                              >
                                Trigger OCS scan
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {/* Pagination */}
              <Group justify="center" p="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                />
              </Group>
            </>
          )}
        </Paper>
      </Stack>

      {/* Device detail drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={
          <Group>
            <IconDeviceDesktop size={20} />
            <Text fw={600}>Device Details</Text>
          </Group>
        }
        size="lg"
      >
        {selectedDevice && <DeviceDrawerContent deviceId={selectedDevice} onOpenCredentials={openCredentialsModal} />}
      </Drawer>

      {/* Add to Group Modal */}
      <Modal
        opened={groupModalOpened}
        onClose={() => {
          closeGroupModal();
          setSelectedGroupId(null);
        }}
        title={
          <Group gap="xs">
            <IconUsers size={20} />
            <Text fw={600}>Add to Device Group</Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Add {selectedRows.length} selected device{selectedRows.length !== 1 ? 's' : ''} to a group.
          </Text>

          {groupsData?.groups && groupsData.groups.length > 0 ? (
            <Select
              label="Select Group"
              placeholder="Choose a group..."
              data={groupsData.groups.map((g) => ({
                value: g.id,
                label: `${g.name} (${g.deviceCount} devices)`,
              }))}
              value={selectedGroupId}
              onChange={setSelectedGroupId}
              searchable
            />
          ) : (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              No groups exist yet. Create a group first in the Groups page.
            </Alert>
          )}

          <Group justify="space-between">
            <Button
              variant="subtle"
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                closeGroupModal();
                navigate('/groups');
              }}
            >
              Create New Group
            </Button>
            <Group gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  closeGroupModal();
                  setSelectedGroupId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddToGroup}
                loading={addToGroup.isPending}
                disabled={!selectedGroupId}
              >
                Add to Group
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Device Credentials Modal */}
      <DeviceCredentialsModal
        opened={credentialsModalOpened}
        onClose={closeCredentialsModal}
        device={selectedDevice ? { id: selectedDevice, hostname: devices.find(d => d.id === selectedDevice)?.hostname || null } : null}
      />
    </>
  );
}

// Device drawer content component
function DeviceDrawerContent({ deviceId, onOpenCredentials }: { deviceId: string; onOpenCredentials: () => void }) {
  const { data: device, isLoading, isError, refetch } = useDevice(deviceId);
  const { data: sites } = useSites();
  const { data: rooms } = useRooms((device as any)?.siteId || undefined);
  const { data: softwareData, isLoading: softwareLoading } = useDeviceSoftware(deviceId);
  const { data: driftData, isLoading: driftLoading } = useDeviceDrift(deviceId);
  const { data: agents } = useRelayAgents();
  const { data: credentials } = useDeviceCredentials(deviceId);
  const triggerRescan = useTriggerRescan();
  const updateSite = useUpdateDeviceSite();
  const updateRoom = useUpdateDeviceRoom();
  const downloadScript = useDownloadRemediationScript();
  const deployRemediation = useDeployRemediation();
  const setSoftwareStatus = useSetSoftwareStatus();

  // Remediation modal state
  const [remediationModalOpen, setRemediationModalOpen] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState<SoftwareItem | null>(null);

  // Rescan modal state
  const [rescanModalOpen, setRescanModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  // Software filter state
  const [softwareFilter, setSoftwareFilter] = useState<'all' | 'allowed' | 'dangerous' | 'unknown'>('all');

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Handle opening remediation modal
  const handleRemediateClick = (sw: SoftwareItem) => {
    setSelectedSoftware(sw);
    setRemediationModalOpen(true);
  };

  // Handle setting software status override
  const handleSetSoftwareStatus = async (softwareName: string, publisher: string | null, newStatus: 'allowed' | 'dangerous' | 'unknown') => {
    try {
      await setSoftwareStatus.mutateAsync({ softwareName, publisher, status: newStatus });
      notifications.show({
        title: 'Status updated',
        message: `${softwareName} has been marked as ${newStatus}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to update status',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  // Handle download script
  const handleDownloadScript = async (format: 'powershell' | 'batch') => {
    if (!selectedSoftware) return;
    try {
      await downloadScript.mutateAsync({
        softwareName: selectedSoftware.name,
        publisher: selectedSoftware.publisher,
        format,
      });
      notifications.show({
        title: 'Script downloaded',
        message: `Remediation script for ${selectedSoftware.name} has been downloaded`,
        color: 'green',
      });
      setRemediationModalOpen(false);
    } catch (error) {
      notifications.show({
        title: 'Download failed',
        message: error instanceof Error ? error.message : 'Failed to download script',
        color: 'red',
      });
    }
  };

  // Handle remote deployment
  const handleDeployRemotely = async () => {
    if (!selectedSoftware) return;
    try {
      const result = await deployRemediation.mutateAsync({
        deviceId,
        softwareName: selectedSoftware.name,
        publisher: selectedSoftware.publisher,
      });
      notifications.show({
        title: 'Remediation deployed',
        message: result.note,
        color: 'green',
      });
      setRemediationModalOpen(false);
    } catch (error) {
      notifications.show({
        title: 'Deployment failed',
        message: error instanceof Error ? error.message : 'Failed to deploy remediation',
        color: 'red',
      });
    }
  };

  // Handle site change
  const handleSiteChange = async (siteId: string | null) => {
    try {
      await updateSite.mutateAsync({ deviceId, siteId });
      refetch();
      notifications.show({
        title: 'Site updated',
        message: 'Device site assignment has been updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to update site',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  // Handle room change
  const handleRoomChange = async (roomId: string | null) => {
    try {
      await updateRoom.mutateAsync({ deviceId, roomId });
      refetch();
      notifications.show({
        title: 'Room updated',
        message: 'Device room assignment has been updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to update room',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  // Format relative time
  const formatLastSeen = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  // Check if data is stale (older than 24 hours)
  const isDataStale = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 24;
  };

  // Open rescan modal - but first check if credentials are configured
  const handleOpenRescanModal = () => {
    if (!credentials?.hasCredentials) {
      // No credentials - open credentials modal first
      notifications.show({
        title: 'Credentials Required',
        message: 'Please configure WinRM credentials before triggering a rescan.',
        color: 'yellow',
      });
      onOpenCredentials();
      return;
    }
    // Credentials exist - open agent selection modal
    setRescanModalOpen(true);
  };

  // Execute rescan with selected agent
  const handleExecuteRescan = async () => {
    if (!selectedAgent) {
      notifications.show({
        title: 'Select an agent',
        message: 'Please select a relay agent to execute the rescan',
        color: 'yellow',
      });
      return;
    }

    try {
      await triggerRescan.mutateAsync({ deviceId, agentId: selectedAgent });
      notifications.show({
        title: 'Rescan triggered',
        message: 'OCS inventory rescan has been requested',
        color: 'green',
      });
      setRescanModalOpen(false);
      setSelectedAgent(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isCredentialsError = errorMessage.toLowerCase().includes('credentials') || errorMessage.toLowerCase().includes('winrm');

      notifications.show({
        title: 'Failed to trigger rescan',
        message: isCredentialsError
          ? 'WinRM credentials are not configured. Click "Set Credentials" to configure them.'
          : errorMessage,
        color: 'red',
      });

      // If credentials error, close rescan modal and open credentials modal
      if (isCredentialsError) {
        setRescanModalOpen(false);
        onOpenCredentials();
      }
    }
  };

  if (isLoading) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={200} />
        <Skeleton height={100} />
      </Stack>
    );
  }

  if (isError || !device) {
    return (
      <Alert color="red" icon={<IconX size={16} />}>
        Failed to load device details
      </Alert>
    );
  }

  const staleData = isDataStale(device.lastSeenAt);
  const hw = device.hardwareProfile;

  return (
    <Tabs defaultValue="overview">
      <Tabs.List>
        <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
          Overview
        </Tabs.Tab>
        <Tabs.Tab value="identity" leftSection={<IconDeviceDesktop size={14} />}>
          Identity
        </Tabs.Tab>
        <Tabs.Tab value="inventory" leftSection={<IconCpu size={14} />}>
          Inventory
        </Tabs.Tab>
        <Tabs.Tab value="asset" leftSection={<IconTag size={14} />}>
          Asset
        </Tabs.Tab>
        <Tabs.Tab value="drift" leftSection={<IconAlertTriangle size={14} />}>
          Drift
        </Tabs.Tab>
        <Tabs.Tab value="fog" leftSection={<IconCloud size={14} />}>
          FOG
        </Tabs.Tab>
        <Tabs.Tab value="software" leftSection={<IconPackage size={14} />}>
          Software
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="overview" pt="md">
        <Stack gap="md">
          {staleData && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="yellow"
              title="Stale Data Warning"
              withCloseButton={false}
            >
              <Group justify="space-between" align="center">
                <Text size="sm">OCS inventory data is outdated. Consider triggering a rescan.</Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={handleOpenRescanModal}
                >
                  Rescan
                </Button>
              </Group>
            </Alert>
          )}

          {device.isRetired && (
            <Alert icon={<IconArchive size={16} />} color="gray" title="Retired Device">
              This device has been marked as retired.
            </Alert>
          )}

          <Grid>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed">
                  Hostname
                </Text>
                <Text fw={500}>{device.hostname}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed" mb="xs">
                  Site
                </Text>
                <Select
                  placeholder="Select site..."
                  data={sites?.map(s => ({ value: s.id, label: s.name })) || []}
                  value={device.siteId || null}
                  onChange={(value) => handleSiteChange(value)}
                  clearable
                  searchable
                  size="sm"
                  disabled={updateSite.isPending}
                />
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed" mb="xs">
                  Room
                </Text>
                <Select
                  placeholder="Select room..."
                  data={rooms?.map(r => ({ value: r.id, label: `${r.roomCode} - ${r.roomName}` })) || []}
                  value={(device as any).roomId || null}
                  onChange={(value) => handleRoomChange(value)}
                  clearable
                  searchable
                  size="sm"
                  disabled={updateRoom.isPending || !device.siteId}
                />
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed">
                  Manufacturer / Model
                </Text>
                <Text fw={500}>{device.manufacturer || 'Unknown'}</Text>
                <Text size="sm" c="dimmed">{device.model || ''}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed">
                  Device Type
                </Text>
                <Badge variant="light" size="sm">{device.deviceType}</Badge>
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed">
                  Last Seen
                </Text>
                <Text fw={500}>{formatLastSeen(device.lastSeenAt)}</Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={6}>
              <Card>
                <Text size="sm" c="dimmed">
                  Data Quality
                </Text>
                <Group gap="xs">
                  <Progress
                    value={device.dataQualityScore}
                    size="sm"
                    w={60}
                    color={device.dataQualityScore >= 80 ? 'green' : device.dataQualityScore >= 60 ? 'yellow' : 'red'}
                  />
                  <Text fw={500}>{device.dataQualityScore}%</Text>
                </Group>
              </Card>
            </Grid.Col>
            <Grid.Col span={12}>
              <Card>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" c="dimmed">
                      Device Actions
                    </Text>
                    <Text size="xs" c="dimmed">
                      Edit device details or configure remote management
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconEdit size={14} />}
                      onClick={() => setEditModalOpen(true)}
                    >
                      Edit Device
                    </Button>
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconKey size={14} />}
                      onClick={onOpenCredentials}
                    >
                      Set Credentials
                    </Button>
                  </Group>
                </Group>
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="identity" pt="md">
        <Stack gap="md">
          <Card>
            <Text size="sm" c="dimmed" mb="xs">
              Identity Confidence
            </Text>
            <StatusBadge type="identityConfidence" value={device.identityConfidence} />
          </Card>

          <Card>
            <Text size="sm" c="dimmed" mb="xs">
              Matched Identifiers
            </Text>
            <Stack gap="xs">
              {device.serialNumber && (
                <Group gap="xs">
                  <IconCheck size={14} color="green" />
                  <Text size="sm">Serial: {device.serialNumber}</Text>
                </Group>
              )}
              {device.primaryMacAddress && (
                <Group gap="xs">
                  <IconCheck size={14} color="green" />
                  <Text size="sm">Primary MAC: {device.primaryMacAddress}</Text>
                </Group>
              )}
              {device.systemUuid && (
                <Group gap="xs">
                  <IconCheck size={14} color="green" />
                  <Text size="sm">BIOS UUID: {device.systemUuid.substring(0, 13)}...</Text>
                </Group>
              )}
            </Stack>
          </Card>

          <Card>
            <Text size="sm" c="dimmed" mb="xs">
              Identity Hash
            </Text>
            <Text size="xs" ff="monospace">
              {device.identityHash}
            </Text>
          </Card>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="inventory" pt="md">
        <Stack gap="md">
          {hw ? (
            <>
              <Card>
                <Text size="sm" fw={500} mb="sm">
                  Hardware
                </Text>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">CPU</Text>
                    <Text size="sm">{hw.cpuModel} ({hw.cpuCores} cores)</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">RAM</Text>
                    <Text size="sm">{Math.round(hw.ramMb / 1024)} GB</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Disk</Text>
                    <Text size="sm">{hw.diskGb} GB</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Manufacturer</Text>
                    <Text size="sm">{hw.manufacturer}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Model</Text>
                    <Text size="sm">{hw.model}</Text>
                  </Group>
                </Stack>
              </Card>

              <Card>
                <Text size="sm" fw={500} mb="sm">
                  Operating System
                </Text>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">OS</Text>
                    <Text size="sm">{device.osName || 'Unknown'}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Version</Text>
                    <Text size="sm">{device.osVersion || 'Unknown'}</Text>
                  </Group>
                  {device.osBuild && (
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Build</Text>
                      <Text size="sm">{device.osBuild}</Text>
                    </Group>
                  )}
                  {device.osArchitecture && (
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Architecture</Text>
                      <Text size="sm">{device.osArchitecture}</Text>
                    </Group>
                  )}
                </Stack>
              </Card>

              <Card>
                <Text size="sm" fw={500} mb="sm">
                  Network
                </Text>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">IP Address</Text>
                    <Text size="sm" ff="monospace">{device.ipAddress || 'Unknown'}</Text>
                  </Group>
                  {device.subnet && (
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Subnet</Text>
                      <Text size="sm" ff="monospace">{device.subnet}</Text>
                    </Group>
                  )}
                </Stack>
              </Card>
            </>
          ) : (
            <Text c="dimmed" size="sm">No hardware profile available</Text>
          )}

          {device.networkInterfaces && device.networkInterfaces.length > 0 && (
            <Card>
              <Text size="sm" fw={500} mb="sm">
                Network Interfaces
              </Text>
              <Stack gap="xs">
                {device.networkInterfaces.map((iface) => (
                  <Group key={iface.id} justify="space-between">
                    <Group gap="xs">
                      <IconNetwork size={14} />
                      <Text size="sm">{iface.interfaceName}</Text>
                      {iface.isPrimary && (
                        <Badge size="xs" color="blue" variant="light">Primary</Badge>
                      )}
                    </Group>
                    <Text size="xs" ff="monospace">{iface.macAddress}</Text>
                  </Group>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="asset" pt="md">
        <Stack gap="md">
          <Card>
            <Text size="sm" fw={500} mb="sm">
              Asset Information
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Asset Tag</Text>
                <Text size="sm" ff="monospace">{device.assetTag || 'Not assigned'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Device Name</Text>
                <Text size="sm">{device.deviceName || device.hostname}</Text>
              </Group>
            </Stack>
          </Card>

          <Card>
            <Text size="sm" fw={500} mb="sm">
              Lifecycle
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Purchase Date</Text>
                <Text size="sm">{device.purchaseDate || 'Unknown'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Warranty Expiry</Text>
                <Text size="sm">{device.warrantyExpiry || 'Unknown'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Status</Text>
                {device.isRetired ? (
                  <Badge color="gray" variant="light">Retired</Badge>
                ) : device.isActive ? (
                  <Badge color="green" variant="light">Active</Badge>
                ) : (
                  <Badge color="yellow" variant="light">Inactive</Badge>
                )}
              </Group>
            </Stack>
          </Card>

          <Card>
            <Text size="sm" fw={500} mb="sm">
              Timestamps
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Created</Text>
                <Text size="sm">{device.createdAt ? new Date(device.createdAt).toLocaleString() : 'Unknown'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Last Updated</Text>
                <Text size="sm">{device.updatedAt ? new Date(device.updatedAt).toLocaleString() : 'Unknown'}</Text>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="drift" pt="md">
        <Stack gap="md">
          {driftLoading ? (
            <Stack gap="sm">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} height={60} />
              ))}
            </Stack>
          ) : driftData?.drift ? (
            <>
              {/* Drift Severity Alert */}
              {driftData.drift.severity === 'none' ? (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  No drift detected. Device matches baseline.
                </Alert>
              ) : (
                <Alert
                  color={driftData.drift.severity === 'high' ? 'red' : driftData.drift.severity === 'medium' ? 'yellow' : 'blue'}
                  icon={<IconAlertTriangle size={16} />}
                >
                  {driftData.drift.severity === 'high'
                    ? 'High severity drift detected. Immediate action recommended.'
                    : driftData.drift.severity === 'medium'
                    ? 'Medium severity drift detected. Review changes.'
                    : 'Low severity drift detected.'}
                </Alert>
              )}

              {/* Drift Summary Cards */}
              <Grid>
                <Grid.Col span={3}>
                  <Card style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="red">{driftData.drift.counts.high}</Text>
                    <Text size="xs" c="dimmed">High</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="yellow">{driftData.drift.counts.medium}</Text>
                    <Text size="xs" c="dimmed">Medium</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700} c="blue">{driftData.drift.counts.low}</Text>
                    <Text size="xs" c="dimmed">Low</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card style={{ textAlign: 'center' }}>
                    <Text size="xl" fw={700}>{driftData.drift.counts.total}</Text>
                    <Text size="xs" c="dimmed">Total Events</Text>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Recent Change Events */}
              {driftData.drift.recentEvents.length > 0 ? (
                <Card>
                  <Text size="sm" fw={500} mb="sm">
                    Recent Changes ({driftData.drift.recentEvents.length})
                  </Text>
                  <Stack gap="xs" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {driftData.drift.recentEvents.map((event: DriftChangeEvent) => (
                      <Paper
                        key={event.id}
                        p="xs"
                        style={{
                          borderLeft: `3px solid ${
                            event.severity === 'high'
                              ? 'var(--mantine-color-red-6)'
                              : event.severity === 'medium'
                              ? 'var(--mantine-color-yellow-6)'
                              : 'var(--mantine-color-blue-6)'
                          }`,
                          backgroundColor:
                            event.severity === 'high'
                              ? 'var(--mantine-color-red-0)'
                              : event.severity === 'medium'
                              ? 'var(--mantine-color-yellow-0)'
                              : 'var(--mantine-color-blue-0)',
                        }}
                      >
                        <Group justify="space-between" mb={4}>
                          <Group gap="xs">
                            <Badge
                              size="xs"
                              color={event.severity === 'high' ? 'red' : event.severity === 'medium' ? 'yellow' : 'blue'}
                            >
                              {event.severity}
                            </Badge>
                            <Badge size="xs" variant="light" color="gray">
                              {event.changeCategory}
                            </Badge>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {new Date(event.changedAt).toLocaleString()}
                          </Text>
                        </Group>
                        <Text size="sm" fw={500}>{event.eventType}</Text>
                        {event.fields && Object.keys(event.fields).length > 0 && (
                          <Stack gap={2} mt={4}>
                            {Object.entries(event.fields).slice(0, 3).map(([field, change]) => (
                              <Group key={field} gap="xs">
                                <Text size="xs" c="dimmed">{field}:</Text>
                                <Text size="xs" c="red" td="line-through">{String((change as {old: unknown}).old || 'null')}</Text>
                                <Text size="xs">→</Text>
                                <Text size="xs" c="green">{String((change as {new: unknown}).new || 'null')}</Text>
                              </Group>
                            ))}
                            {Object.keys(event.fields).length > 3 && (
                              <Text size="xs" c="dimmed">+{Object.keys(event.fields).length - 3} more fields</Text>
                            )}
                          </Stack>
                        )}
                        <Text size="xs" c="dimmed" mt={4}>
                          Detected by: {event.detectedBy}
                        </Text>
                      </Paper>
                    ))}
                  </Stack>
                </Card>
              ) : (
                <Card>
                  <Text size="sm" c="dimmed" ta="center">
                    No recent change events recorded.
                  </Text>
                </Card>
              )}
            </>
          ) : (
            <Alert color="gray" icon={<IconInfoCircle size={16} />}>
              Drift detection data is not available for this device.
            </Alert>
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="fog" pt="md">
        <Stack gap="md">
          <Card>
            <Text size="sm" fw={500} mb="sm">
              FOG Host Mapping
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">FOG Host ID</Text>
                <Text size="sm">{device.fogHostId || 'Not mapped'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Assigned Group</Text>
                {device.fogGroupName ? (
                  <Badge variant="light">{device.fogGroupName}</Badge>
                ) : (
                  <Text size="sm" c="dimmed">None</Text>
                )}
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Target Image</Text>
                <Text size="sm">{device.fogImageName || 'None'}</Text>
              </Group>
            </Stack>
          </Card>

          <Card>
            <Text size="sm" fw={500} mb="sm">
              OCS Inventory
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">OCS Device ID</Text>
                <Text size="sm">{device.ocsDeviceId || 'Not found'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Last OCS Sync</Text>
                <Text size="sm">{device.ocsLastSync ? new Date(device.ocsLastSync).toLocaleString() : 'Never'}</Text>
              </Group>
            </Stack>
          </Card>

          <Card>
            <Text size="sm" fw={500} mb="sm">
              Sync Status
            </Text>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Last FOG Sync</Text>
                <Text size="sm">{device.fogLastSync ? new Date(device.fogLastSync).toLocaleString() : 'Never'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">OCS Data Version</Text>
                <Text size="sm">{device.ocsDataVersion ?? 0}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">FOG Data Version</Text>
                <Text size="sm">{device.fogDataVersion ?? 0}</Text>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="software" pt="md">
        <Stack gap="md">
          {softwareLoading ? (
            <Stack gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={40} />
              ))}
            </Stack>
          ) : softwareData ? (
            <>
              {/* Software Summary - Clickable Filter Cards */}
              <Grid>
                <Grid.Col span={3}>
                  <Card
                    style={{
                      textAlign: 'center',
                      cursor: 'pointer',
                      outline: softwareFilter === 'all' ? '2px solid var(--mantine-color-blue-5)' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setSoftwareFilter('all')}
                  >
                    <Text size="xl" fw={700}>{softwareData.summary.total}</Text>
                    <Text size="xs" c="dimmed">Total</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card
                    style={{
                      textAlign: 'center',
                      cursor: 'pointer',
                      outline: softwareFilter === 'allowed' ? '2px solid var(--mantine-color-green-5)' : 'none',
                      borderColor: 'var(--mantine-color-green-5)',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setSoftwareFilter(softwareFilter === 'allowed' ? 'all' : 'allowed')}
                  >
                    <Group gap={4} justify="center">
                      <IconShieldCheck size={16} color="var(--mantine-color-green-6)" />
                      <Text size="xl" fw={700} c="green">{softwareData.summary.allowed}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">Allowed</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card
                    style={{
                      textAlign: 'center',
                      cursor: 'pointer',
                      outline: softwareFilter === 'dangerous' ? '2px solid var(--mantine-color-red-5)' : 'none',
                      borderColor: 'var(--mantine-color-red-5)',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setSoftwareFilter(softwareFilter === 'dangerous' ? 'all' : 'dangerous')}
                  >
                    <Group gap={4} justify="center">
                      <IconShieldX size={16} color="var(--mantine-color-red-6)" />
                      <Text size="xl" fw={700} c="red">{softwareData.summary.dangerous}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">Dangerous</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card
                    style={{
                      textAlign: 'center',
                      cursor: 'pointer',
                      outline: softwareFilter === 'unknown' ? '2px solid var(--mantine-color-gray-5)' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setSoftwareFilter(softwareFilter === 'unknown' ? 'all' : 'unknown')}
                  >
                    <Group gap={4} justify="center">
                      <IconQuestionMark size={16} color="var(--mantine-color-gray-6)" />
                      <Text size="xl" fw={700} c="dimmed">{softwareData.summary.unknown}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">Unknown</Text>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Dangerous Software Alert */}
              {softwareData.summary.dangerous > 0 && (
                <Alert
                  icon={<IconShieldX size={16} />}
                  color="red"
                  title="Dangerous Software Detected"
                >
                  This device has {softwareData.summary.dangerous} prohibited software installed that may pose security risks.
                </Alert>
              )}

              {/* Software List */}
              <Card>
                <Group justify="space-between" mb="sm">
                  <Text size="sm" fw={500}>
                    {softwareFilter === 'all'
                      ? `Installed Software (${softwareData.software.length})`
                      : `${softwareFilter.charAt(0).toUpperCase() + softwareFilter.slice(1)} Software (${softwareData.software.filter(s => s.status === softwareFilter).length})`}
                  </Text>
                  {softwareFilter !== 'all' && (
                    <Badge
                      variant="light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSoftwareFilter('all')}
                      rightSection={<IconX size={12} />}
                    >
                      Clear filter
                    </Badge>
                  )}
                </Group>
                <Stack gap="xs" style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {softwareData.software
                    .filter(sw => softwareFilter === 'all' || sw.status === softwareFilter)
                    .map((sw) => (
                    <Group
                      key={sw.id}
                      justify="space-between"
                      p="xs"
                      style={{
                        borderRadius: 'var(--mantine-radius-sm)',
                        backgroundColor: sw.status === 'dangerous'
                          ? 'var(--mantine-color-red-0)'
                          : sw.status === 'allowed'
                          ? 'var(--mantine-color-green-0)'
                          : 'var(--mantine-color-gray-0)',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <Group gap="xs">
                          {sw.status === 'allowed' && <IconShieldCheck size={14} color="var(--mantine-color-green-6)" />}
                          {sw.status === 'dangerous' && <IconShieldX size={14} color="var(--mantine-color-red-6)" />}
                          {sw.status === 'unknown' && <IconQuestionMark size={14} color="var(--mantine-color-gray-6)" />}
                          <Text size="sm" fw={500}>{sw.name}</Text>
                        </Group>
                        <Group gap="xs" mt={2}>
                          {sw.version && (
                            <Text size="xs" c="dimmed">v{sw.version}</Text>
                          )}
                          {sw.publisher && (
                            <Text size="xs" c="dimmed">• {sw.publisher}</Text>
                          )}
                        </Group>
                        {sw.status === 'dangerous' && sw.reason && (
                          <Text size="xs" c="red" mt={2}>{sw.reason}</Text>
                        )}
                      </div>
                      <Group gap="xs">
                        {sw.status === 'dangerous' && sw.severity && (
                          <Badge
                            color={sw.severity === 'critical' ? 'red' : sw.severity === 'high' ? 'orange' : sw.severity === 'medium' ? 'yellow' : 'gray'}
                            size="xs"
                          >
                            {sw.severity}
                          </Badge>
                        )}
                        {sw.category && sw.status !== 'dangerous' && (
                          <Badge color="gray" variant="light" size="xs">
                            {sw.category}
                          </Badge>
                        )}
                        {sw.status === 'dangerous' && (
                          <ActionIcon
                            variant="light"
                            color="red"
                            size="sm"
                            title="Remove this software"
                            onClick={() => handleRemediateClick(sw)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                        {/* Status Override Menu */}
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm" title="Change status">
                              <IconSettings size={14} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Label>Set status for all instances</Menu.Label>
                            <Menu.Item
                              leftSection={<IconShieldCheck size={14} color="var(--mantine-color-green-6)" />}
                              disabled={sw.status === 'allowed'}
                              onClick={() => handleSetSoftwareStatus(sw.name, sw.publisher, 'allowed')}
                            >
                              Mark as Allowed
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconShieldX size={14} color="var(--mantine-color-red-6)" />}
                              disabled={sw.status === 'dangerous'}
                              onClick={() => handleSetSoftwareStatus(sw.name, sw.publisher, 'dangerous')}
                            >
                              Mark as Dangerous
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconQuestionMark size={14} color="var(--mantine-color-gray-6)" />}
                              disabled={sw.status === 'unknown'}
                              onClick={() => handleSetSoftwareStatus(sw.name, sw.publisher, 'unknown')}
                            >
                              Mark as Unknown
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Card>

              {softwareData.lastUpdated && (
                <Text size="xs" c="dimmed" ta="center">
                  Last updated: {new Date(softwareData.lastUpdated).toLocaleString()}
                </Text>
              )}
            </>
          ) : (
            <Alert color="gray" icon={<IconPackage size={16} />}>
              No software inventory available for this device.
            </Alert>
          )}
        </Stack>
      </Tabs.Panel>

      {/* Remediation Modal */}
      <Modal
        opened={remediationModalOpen}
        onClose={() => setRemediationModalOpen(false)}
        title={
          <Group gap="xs">
            <IconShieldX size={20} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Remove Dangerous Software</Text>
          </Group>
        }
        size="md"
      >
        {selectedSoftware && (
          <Stack gap="md">
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={500}>{selectedSoftware.name}</Text>
              {selectedSoftware.publisher && (
                <Text size="xs" c="dimmed">Publisher: {selectedSoftware.publisher}</Text>
              )}
              {selectedSoftware.reason && (
                <Text size="xs" mt="xs">{selectedSoftware.reason}</Text>
              )}
            </Alert>

            <Text size="sm" c="dimmed">
              Choose how you want to remove this software:
            </Text>

            <Stack gap="sm">
              {/* Download Script Options */}
              <Card withBorder p="sm">
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <IconDownload size={18} />
                    <Text size="sm" fw={500}>Download Removal Script</Text>
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mb="sm">
                  Download a script to run manually on the device.
                </Text>
                <Group gap="xs">
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconDownload size={14} />}
                    onClick={() => handleDownloadScript('powershell')}
                    loading={downloadScript.isPending}
                  >
                    PowerShell (.ps1)
                  </Button>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconDownload size={14} />}
                    onClick={() => handleDownloadScript('batch')}
                    loading={downloadScript.isPending}
                  >
                    Batch (.bat)
                  </Button>
                </Group>
              </Card>

              {/* Remote Deployment Option */}
              <Card withBorder p="sm">
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <IconRocket size={18} />
                    <Text size="sm" fw={500}>Deploy via FOG Snapin</Text>
                  </Group>
                  {!device?.fogHostId && (
                    <Badge color="gray" size="xs">Unavailable</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed" mb="sm">
                  {device?.fogHostId
                    ? 'Automatically deploy the removal script to this device via FOG. The script will run on the next client check-in.'
                    : 'This device is not linked to a FOG host. Remote deployment is not available.'}
                </Text>
                <Button
                  variant="filled"
                  color="red"
                  size="xs"
                  leftSection={<IconRocket size={14} />}
                  onClick={handleDeployRemotely}
                  loading={deployRemediation.isPending}
                  disabled={!device?.fogHostId}
                >
                  Deploy Removal Script
                </Button>
              </Card>
            </Stack>
          </Stack>
        )}
      </Modal>

      {/* Rescan Agent Selection Modal */}
      <Modal
        opened={rescanModalOpen}
        onClose={() => {
          setRescanModalOpen(false);
          setSelectedAgent(null);
        }}
        title={
          <Group gap="xs">
            <IconRefresh size={20} />
            <Text fw={600}>Trigger OCS Rescan</Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <Alert color="blue" variant="light">
            <Text size="sm">
              Select a relay agent to execute the OCS inventory rescan on <strong>{device?.hostname}</strong>
            </Text>
          </Alert>

          <Select
            label="Relay Agent"
            placeholder="Select agent to execute rescan"
            data={
              agents?.map((agent: RelayAgent) => ({
                value: agent.id,
                label: `${agent.name} (${agent.status})`,
              })) || []
            }
            value={selectedAgent}
            onChange={setSelectedAgent}
            leftSection={<IconRouter size={16} />}
            searchable
          />

          {agents?.length === 0 && (
            <Alert color="yellow" variant="light">
              No relay agents available. Make sure at least one agent is running.
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setRescanModalOpen(false);
                setSelectedAgent(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExecuteRescan}
              loading={triggerRescan.isPending}
              disabled={!selectedAgent}
              leftSection={<IconRefresh size={16} />}
            >
              Trigger Rescan
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Device Edit Modal */}
      <DeviceEditModal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          refetch();
        }}
        device={device ? {
          id: deviceId,
          hostname: device.hostname,
          ipAddress: device.ipAddress || null,
          primaryMacAddress: device.primaryMacAddress || null,
        } : null}
      />
    </Tabs>
  );
}
